import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { useAuth }          from '@/features/auth';
import { companyService }   from '@/lib/companyService';
// Phase 77 — session-scoped workspace-selection flag. Flipping it on the
// LEGACY setter as well keeps direct legacy callers (WorkspacesPage card
// click, ModuleGuard auto-switch, UnauthorizedModulePage picker, etc.)
// in lock-step with the Phase 76 context setter.
import { markWorkspaceSelected } from '@/lib/auth';
import type { Company, ModulePermissions, AppModule } from '@/types/database';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface CompanyWithMeta extends Company {
  /** The current user's role in this company: 'admin' | 'member' */
  role?: 'admin' | 'member';
  /** Fine-grained per-user module permissions for this company. */
  modulePermissions?: ModulePermissions | null;
}

interface CompanyContextType {
  activeCompanyId:          string;
  activeCompany:            CompanyWithMeta | null;
  companies:                CompanyWithMeta[];
  loadingCompanies:         boolean;
  /** The current user's role in the active company ('admin' | 'member' | null). */
  activeCompanyRole:        'admin' | 'member' | null;
  /** Fine-grained per-user module permissions for the active company. */
  activeModulePermissions:  ModulePermissions | null;
  /**
   * ★ NEW — modules unlocked at the company level.
   * Sourced from companies.active_modules (TEXT[]).
   * A module must appear here before per-user permissions are even checked.
   */
  activeModules:            AppModule[];
  setActiveCompanyId:       (id: string) => void;
  refreshCompanies:         () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

const STORAGE_KEY = 'axelio_active_company_id';

// ─── Provider ─────────────────────────────────────────────────────────────────

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [companies,        setCompanies]        = useState<CompanyWithMeta[]>([]);
  const [activeCompanyId,  setActiveCompanyIdState] = useState<string>('');
  // ★ Start as TRUE so the sidebar/route-guards don't flash before the first DB fetch.
  // Set to false once refreshCompanies() completes (success OR error).
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  const setActiveCompanyId = useCallback((id: string) => {
    setActiveCompanyIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
      // Phase 77 — explicit selection flips the per-tab gate flag so
      // <PostLoginGate> allows entry into /app/*. Fresh logins (no flag)
      // are forced through the Workspaces portal regardless of whether
      // axelio_active_company_id is cached in localStorage.
      markWorkspaceSelected();
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const refreshCompanies = useCallback(async () => {
    if (!user?.id) {
      setLoadingCompanies(false);
      return;
    }
    setLoadingCompanies(true);
    try {
      const { data, error } = await companyService.getUserCompanies(user.id);
      // ⚠️  Do NOT use a bare `return` here — it would skip the finally block
      //     and leave loadingCompanies = true forever, hanging every route guard.
      if (error) {
        console.error('CompanyContext: getUserCompanies error:', error);
        return;   // finally will still run and set loadingCompanies = false
      }
      if (!data) return;  // same — finally still runs

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const list: CompanyWithMeta[] = (data as any[])
        .filter((m) => m.company)
        .map((m) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const co = m.company as Record<string, any>;
          return {
            // Spread all company scalar fields
            id:                  co.id,
            legal_name:          co.legal_name,
            country:             co.country,
            jurisdiction:        co.jurisdiction ?? null,
            registration_number: co.registration_number,
            tax_id:              co.tax_id,
            lei:                 co.lei ?? null,
            duns:                co.duns ?? null,
            local_requirements:  co.local_requirements ?? null,
            status:              co.status,
            created_by:          co.created_by,
            created_at:          co.created_at,
            updated_at:          co.created_at,   // companies table has no updated_at — use created_at
            invite_token:        co.invite_token ?? null,
            // ★ active_modules — always normalise to AppModule[]
            active_modules:      (Array.isArray(co.active_modules) ? co.active_modules : []) as AppModule[],
            // Phase 64: B2B backorder global toggle
            allow_b2b_backorders: Boolean(co.allow_b2b_backorders ?? false),
            // CompanyWithMeta extras
            role:              m.role as 'admin' | 'member',
            modulePermissions: (m.module_permissions as ModulePermissions | null) ?? null,
          } satisfies CompanyWithMeta;
        });

      // Debug: log active_modules per company so we can verify the DB data reaches the client
      if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_MODULES) {
        list.forEach(c =>
          console.debug(`[CompanyContext] ${c.legal_name} active_modules:`, c.active_modules),
        );
      }

      setCompanies(list);

      // Restore persisted selection or fall back to first company
      const persisted      = localStorage.getItem(STORAGE_KEY);
      const validPersisted = persisted && list.some((c) => c.id === persisted);
      if (validPersisted) {
        setActiveCompanyIdState(persisted!);
      } else if (list.length > 0 && list[0]) {
        setActiveCompanyIdState(list[0].id);
        localStorage.setItem(STORAGE_KEY, list[0].id);
      }
    } catch (err) {
      console.error('CompanyContext: refreshCompanies error:', err);
    } finally {
      setLoadingCompanies(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      refreshCompanies();
    } else {
      // No authenticated user — clear data and mark loading as done
      // so sidebar/guards don't spin forever on public/login routes.
      setCompanies([]);
      setActiveCompanyIdState('');
      setLoadingCompanies(false);
    }
  }, [user?.id, refreshCompanies]);

  const activeCompany = companies.find((c) => c.id === activeCompanyId) ?? null;

  // Derive role / module permissions from the active company record
  const activeCompanyRole:      'admin' | 'member' | null = activeCompany?.role              ?? null;
  const activeModulePermissions: ModulePermissions | null  = activeCompany?.modulePermissions ?? null;
  // ★ NEW: company-level unlocked modules
  const activeModules:           AppModule[]               = activeCompany?.active_modules    ?? [];

  return (
    <CompanyContext.Provider
      value={{
        activeCompanyId,
        activeCompany,
        companies,
        loadingCompanies,
        activeCompanyRole,
        activeModulePermissions,
        activeModules,           // ★ NEW
        setActiveCompanyId,
        refreshCompanies,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within a CompanyProvider');
  return ctx;
}
